"use client";

import { Minus, Plus, Shield, ShoppingCart, Tag, Trash2, Truck } from "lucide-react";
import { useState } from "react";

import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export interface ShoppingCart3Item {
  id: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
  variant?: string;
}

interface ShoppingCart3Labels {
  title?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  continueShopping?: string;
  inStock?: string;
  remove?: string;
  promoPlaceholder?: string;
  apply?: string;
  promoHint?: string;
  promoEmptyError?: string;
  promoInvalidError?: string;
  orderSummary?: string;
  subtotal?: string;
  discount?: string;
  shipping?: string;
  total?: string;
  itemSingular?: string;
  itemPlural?: string;
  checkout?: string;
  helper?: string;
  shippingNote?: string;
  guaranteeNote?: string;
  secureNote?: string;
}

interface ShoppingCart3Props {
  className?: string;
  items?: ShoppingCart3Item[];
  initialItems?: ShoppingCart3Item[];
  onItemsChange?: (items: ShoppingCart3Item[]) => void;
  labels?: ShoppingCart3Labels;
  locale?: string;
  currency?: string;
  validPromoCode?: string;
  discountRate?: number;
  shipping?: number;
}

const DEFAULT_ITEMS: ShoppingCart3Item[] = [
  {
    id: "1",
    name: "Minimalist Beige Sneakers",
    image:
      "https://deifkwefumgah.cloudfront.net/shadcnblocks/block/ecommerce/clothes/Minimalist-Beige-Sneakers-2.png",
    price: 120,
    quantity: 1,
    variant: "Size: EU 36",
  },
];

const defaultLabels: Required<ShoppingCart3Labels> = {
  title: "Shopping Cart",
  emptyTitle: "Your cart is empty",
  emptyDescription: "Looks like you haven't added anything yet.",
  continueShopping: "Continue Shopping",
  inStock: "In Stock",
  remove: "Remove",
  promoPlaceholder: "Promo code",
  apply: "Apply",
  promoHint: "Try SAVE10 for 10% off",
  promoEmptyError: "Please enter a promo code",
  promoInvalidError: "Invalid promo code",
  orderSummary: "Order Summary",
  subtotal: "Subtotal",
  discount: "Discount",
  shipping: "Shipping",
  total: "Total",
  itemSingular: "item",
  itemPlural: "items",
  checkout: "Proceed to Checkout",
  helper: "Taxes calculated at checkout",
  shippingNote: "Free shipping on orders over $150",
  guaranteeNote: "Free 30-day returns",
  secureNote: "Secure checkout",
};

const ShoppingCart3 = ({
  className,
  items: controlledItems,
  initialItems = DEFAULT_ITEMS,
  onItemsChange,
  labels,
  locale = "en-US",
  currency = "USD",
  validPromoCode = "SAVE10",
  discountRate = 0.1,
  shipping = 9.99,
}: ShoppingCart3Props) => {
  const copy = { ...defaultLabels, ...labels };
  const [internalItems, setInternalItems] = useState(initialItems);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const items = controlledItems ?? internalItems;

  const commitItems = (nextItems: ShoppingCart3Item[]) => {
    if (controlledItems) {
      onItemsChange?.(nextItems);
    } else {
      setInternalItems(nextItems);
      onItemsChange?.(nextItems);
    }
  };

  const updateQuantity = (id: string, delta: number) => {
    commitItems(
      items
        .map((item) =>
          item.id === id
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  };

  const removeItem = (id: string) => {
    commitItems(items.filter((item) => item.id !== id));
  };

  const applyPromoCode = () => {
    setPromoError(null);
    if (!promoCode.trim()) {
      setPromoError(copy.promoEmptyError);
      return;
    }
    if (promoCode.toUpperCase() === validPromoCode.toUpperCase()) {
      setAppliedPromo(promoCode.toUpperCase());
      setPromoCode("");
    } else {
      setPromoError(copy.promoInvalidError);
    }
  };

  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const discount = appliedPromo ? subtotal * discountRate : 0;
  const total = subtotal - discount + shipping;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (items.length === 0) {
    return (
      <section className={cn("shadcn-shopping-cart3 py-10", className)}>
        <div className="mx-auto max-w-lg text-center">
          <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-muted">
            <ShoppingCart className="size-8 text-muted-foreground" />
          </div>
          <h1 className="mb-4 text-2xl font-semibold">{copy.emptyTitle}</h1>
          <p className="mb-8 text-muted-foreground">{copy.emptyDescription}</p>
          <Button type="button">{copy.continueShopping}</Button>
        </div>
      </section>
    );
  }

  return (
    <section className={cn("shadcn-shopping-cart3", className)}>
      <h2 className="mb-4 text-lg font-semibold">{copy.title}</h2>

      <div className="grid gap-5">
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="cart-block-row flex gap-3 rounded-lg border bg-card p-3"
            >
              <div className="w-20 shrink-0">
                <AspectRatio ratio={1} className="overflow-hidden rounded-lg bg-muted">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="size-full object-cover"
                  />
                </AspectRatio>
              </div>

              <div className="cart-item-body">
                <div className="cart-item-title-row">
                  <div className="min-w-0">
                    <h3 className="line-clamp-2 text-sm font-semibold">
                      {item.name}
                    </h3>
                    {item.variant && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {item.variant}
                      </p>
                    )}
                    <Badge
                      variant="secondary"
                      className="mt-2 bg-green-100 text-green-700"
                    >
                      {copy.inStock}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
                    type="button"
                    onClick={() => removeItem(item.id)}
                    aria-label={`${copy.remove} ${item.name}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>

                <div className="cart-item-footer">
                  <div className="cart-item-qty">
                    <Button
                      variant="outline"
                      size="icon"
                      className="size-8"
                      type="button"
                      onClick={() => updateQuantity(item.id, -1)}
                    >
                      <Minus className="size-4" />
                    </Button>
                    <span className="w-7 text-center text-sm font-semibold">
                      {item.quantity}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="size-8"
                      type="button"
                      onClick={() => updateQuantity(item.id, 1)}
                    >
                      <Plus className="size-4" />
                    </Button>
                  </div>
                  <div className="cart-item-price">
                    <p className="text-sm font-semibold">
                      {formatPrice(item.price * item.quantity)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatPrice(item.price)}/thùng
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-4 text-base font-semibold">{copy.orderSummary}</h3>

          <div className="mb-4">
            {appliedPromo ? (
              <div className="flex items-center justify-between rounded-md bg-green-50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700">
                    <Tag className="size-3" />
                    {appliedPromo}
                  </Badge>
                  <span className="text-sm text-green-700">
                    {Math.round(discountRate * 100)}% off
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1 text-muted-foreground hover:text-foreground"
                  type="button"
                  onClick={() => setAppliedPromo(null)}
                >
                  {copy.remove}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="cart-promo-input-row">
                  <Input
                    placeholder={copy.promoPlaceholder}
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value);
                      setPromoError(null);
                    }}
                    className="flex-1"
                  />
                  <Button variant="outline" type="button" onClick={applyPromoCode}>
                    {copy.apply}
                  </Button>
                </div>
                {promoError && (
                  <p className="text-sm text-destructive">{promoError}</p>
                )}
                <p className="text-xs text-muted-foreground">{copy.promoHint}</p>
              </div>
            )}
          </div>

          <Separator className="my-4" />

          <div className="cart-summary-list">
            <div className="cart-summary-row">
              <span className="cart-summary-count">
                <ShoppingCart className="size-4" />
                {items.length} {items.length === 1 ? copy.itemSingular : copy.itemPlural}
              </span>
            </div>
            <div className="cart-summary-row">
              <span className="text-muted-foreground">{copy.subtotal}</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="cart-summary-row cart-summary-row--discount">
                <span>{copy.discount}</span>
                <span>-{formatPrice(discount)}</span>
              </div>
            )}
            <div className="cart-summary-row">
              <span className="text-muted-foreground">{copy.shipping}</span>
              <span>{formatPrice(shipping)}</span>
            </div>

            <Separator />

            <div className="cart-summary-row cart-summary-row--total">
              <span>{copy.total}</span>
              <span>{formatPrice(total)}</span>
            </div>
          </div>

          <Button size="lg" className="mt-6 w-full" type="button">
            {copy.checkout}
          </Button>

          <p className="mt-3 text-center text-xs text-muted-foreground">
            {copy.helper}
          </p>

          <Separator className="my-5" />

          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Truck className="size-5 shrink-0" />
              <span>{copy.shippingNote}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Shield className="size-5 shrink-0" />
              <span>{copy.guaranteeNote}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Tag className="size-5 shrink-0" />
              <span>{copy.secureNote}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { ShoppingCart3 };
